import React, { useState } from 'react';

import './CustomGradientLineChart.css';

const DEFAULT_PROPS = {
  width: '200px',
  height: 96,
  viewBox: '0 0 100 100',
  margin: '20px',
  lineGradient: [
    { offset: '10%', color: '#0294F7' },
    { offset: '90%', color: '#01AE66' },
  ],
  fillGradient: [
    { offset: '0%', color: '#00A67E', opacity: 0.3 },
    { offset: '25%', color: '#00A67E', opacity: 0.3 },
    { offset: '100%', color: '#ffffff', opacity: 0 },
  ],
  strokeWidth: 2,
  animationDuration: 2,
  showFill: true,
  animate: true,
  darkMode: false,
  className: '',
  idPrefix: Math.random().toString(36).substr(2, 9),
  onClick: undefined,
  yAxis: undefined,
  xAxis: undefined,
};

/**
 * GradientLineChart - A reusable SVG-based line chart with gradient fills and animation
 *
 * @component
 * @description
 * This component renders an animated line chart with customizable gradients, colors, and animation.
 * Features include:
 * - Smooth line rendering with gradient strokes
 * - Optional filled area under the curve
 * - Reveal animation from left to right
 * - Fully customizable colors and gradients
 * - Responsive design with configurable dimensions
 *
 * @example
 * const data = [
 *   { x: 0, y: 60 },
 *   { x: 50, y: 30 },
 *   { x: 100, y: 80 }
 * ];
 *
 * return (
 *   <GradientLineChart
 *     data={data}
 *     width="300px"
 *     lineGradient={[
 *       { offset: '0%', color: '#ff0000' },
 *       { offset: '100%', color: '#00ff00' }
 *     ]}
 *   />
 * );
 *
 * @example
 * // With X and Y axes (optional — only rendered when props are passed)
 * const data = [
 *   { x: 0, y: 5 },
 *   { x: 20, y: 20 },
 *   { x: 50, y: 40 },
 *   { x: 80, y: 72 },
 *   { x: 100, y: 95 },
 * ];
 *
 * return (
 *   <GradientLineChart
 *     data={data}
 *     width="400px"
 *     height={220}
 *     viewBox="-12 -2 120 115"
 *     yAxis={{
 *       labels: [
 *         { label: '3000', value: 0 },
 *         { label: '3300', value: 25 },
 *         { label: '3600', value: 50 },
 *         { label: '3900', value: 75 },
 *         { label: '4200', value: 100 },
 *       ],
 *       showGridLines: true,
 *       gridLineColor: '#d1d5db',
 *       fontSize: 5.5,
 *     }}
 *     xAxis={{
 *       labels: [
 *         { label: "Jan '26", value: 20 },
 *         { label: "Feb '26", value: 40 },
 *         { label: "Mar '26", value: 65 },
 *         { label: "Apr '26", value: 90 },
 *       ],
 *       showGridLines: false,
 *       fontSize: 5.5,
 *     }}
 *   />
 * );
 */
function GradientLineChart({
  data,
  width = DEFAULT_PROPS.width,
  height = DEFAULT_PROPS.height,
  viewBox = DEFAULT_PROPS.viewBox,
  margin = DEFAULT_PROPS.margin,
  lineGradient = DEFAULT_PROPS.lineGradient,
  fillGradient = DEFAULT_PROPS.fillGradient,
  strokeWidth = DEFAULT_PROPS.strokeWidth,
  animationDuration = DEFAULT_PROPS.animationDuration,
  showFill = DEFAULT_PROPS.showFill,
  animate = DEFAULT_PROPS.animate,
  darkMode = DEFAULT_PROPS.darkMode,
  className = DEFAULT_PROPS.className,
  idPrefix = DEFAULT_PROPS.idPrefix,
  onClick,
  yAxis,
  xAxis,
}: LineChartProps): React.ReactElement {
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    visible: boolean;
  }>({ x: 0, y: 0, visible: false });
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const path = generatePath(data);
  const tooltipCoordinateText = `x: ${tooltip.x.toFixed(
    0
  )}, y: ${tooltip.y.toFixed(0)}`;
  const lineGradientId = `lineGradient-${idPrefix}`;
  const fillGradientId = `fillGradient-${idPrefix}`;
  const clipPathId = `revealClip-${idPrefix}`;

  const handleMouseMove = (e: React.MouseEvent<SVGPathElement>) => {
    const svg = e.currentTarget.ownerSVGElement;

    if (!svg) {
      return;
    }

    const pt = svg.createSVGPoint();

    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const container = e.currentTarget.closest('.gradient-line-chart-container');

    if (container instanceof HTMLElement) {
      const containerRect = container.getBoundingClientRect();

      setTooltip({ x: svgP.x, y: 100 - svgP.y, visible: true });
      setMousePos({
        x: e.clientX - containerRect.left,
        y: e.clientY - containerRect.top,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ x: 0, y: 0, visible: false });
  };

  const handleClick = (e: React.MouseEvent<SVGPathElement>) => {
    if (!onClick) {
      return;
    }

    const svg = e.currentTarget.ownerSVGElement;

    if (!svg) {
      return;
    }

    const pt = svg.createSVGPoint();

    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());

    onClick({ x: svgP.x, y: 100 - svgP.y });
  };

  return (
    <div
      style={{ width, margin }}
      className={`gradient-line-chart-container ${
        darkMode ? 'dark-mode' : ''
      } ${className}`}
      data-testid='gradient-line-chart-container'>
      {tooltip.visible && (
        <div
          className='gradient-line-chart-tooltip'
          data-testid='gradient-line-chart-tooltip'
          style={{
            left: `${mousePos.x}px`,
            top: `${mousePos.y}px`,
          }}>
          {tooltipCoordinateText}
        </div>
      )}
      <svg
        width='100%'
        height={height}
        viewBox={viewBox}
        preserveAspectRatio='none'
        className='gradient-line-chart-svg'
        data-testid='gradient-line-chart-svg'
        role='img'
        aria-label={`Line chart with ${data.length} data points`}>
        {/* <title>{l('Line Chart')}</title> */}
        <desc>
          {data.map((point) => `x: ${point.x}, y: ${point.y}`).join('; ')}
        </desc>
        <defs>
          {/* Line gradient definition */}
          <linearGradient id={lineGradientId} x1='0%' y1='0%' x2='100%' y2='0%'>
            {lineGradient.map((stop) => (
              <stop
                key={`${stop.offset}-${stop.color}`}
                offset={stop.offset}
                stopColor={stop.color}
                stopOpacity={stop.opacity}
              />
            ))}
          </linearGradient>

          {/* Fill gradient definition */}
          {showFill && (
            <linearGradient id={fillGradientId} x1='0' y1='0' x2='0' y2='1'>
              {fillGradient.map((stop) => (
                <stop
                  key={`${stop.offset}-${stop.color}`}
                  offset={stop.offset}
                  stopColor={stop.color}
                  stopOpacity={stop.opacity}
                />
              ))}
            </linearGradient>
          )}

          {/* Animation clip path */}
          {animate && (
            <clipPath id={clipPathId}>
              <rect x='0' y='0' width='0' height='100'>
                <animate
                  attributeName='width'
                  from='0'
                  to='100'
                  dur={`${animationDuration}s`}
                  fill='freeze'
                  calcMode='linear'
                  keyTimes='0;1'
                  values='0;100'
                />
              </rect>
            </clipPath>
          )}
        </defs>

        {/* Y-axis: horizontal grid lines + labels on the left */}
        {yAxis?.labels.map((tick) => {
          const yPos = 100 - tick.value;
          const showGrid = yAxis.showGridLines !== false;
          const gridColor = yAxis.gridLineColor ?? '#2a3444';
          const labelColor =
            yAxis.labelColor ?? (darkMode ? '#ffffff' : '#374151');
          const fontSize = yAxis.fontSize ?? 5.5;

          return (
            <g key={`y-${tick.value}`}>
              {showGrid && (
                <line
                  x1='0'
                  y1={yPos}
                  x2='100'
                  y2={yPos}
                  stroke={gridColor}
                  strokeWidth='0.3'
                  strokeDasharray='1.5,1'
                  vectorEffect='non-scaling-stroke'
                />
              )}
              <text
                x='-2'
                y={yPos}
                fill={labelColor}
                fontSize={fontSize}
                fontFamily='Rubik, sans-serif'
                fontWeight={400}
                textAnchor='end'
                dominantBaseline='middle'>
                {tick.label}
              </text>
            </g>
          );
        })}

        {/* X-axis: vertical grid lines + labels at the bottom */}
        {xAxis?.labels.map((tick) => {
          const xPos = tick.value;
          const showGrid = xAxis.showGridLines !== false;
          const gridColor = xAxis.gridLineColor ?? '#2a3444';
          const labelColor =
            xAxis.labelColor ?? (darkMode ? '#ffffff' : '#374151');
          const fontSize = xAxis.fontSize ?? 5.5;

          return (
            <g key={`x-${tick.value}`}>
              {showGrid && (
                <line
                  x1={xPos}
                  y1='0'
                  x2={xPos}
                  y2='100'
                  stroke={gridColor}
                  strokeWidth='0.3'
                  strokeDasharray='1.5,1'
                  vectorEffect='non-scaling-stroke'
                />
              )}
              <text
                x={xPos}
                y='108'
                fill={labelColor}
                fontSize={fontSize}
                fontFamily='Rubik, sans-serif'
                fontWeight={400}
                textAnchor='middle'>
                {tick.label}
              </text>
            </g>
          );
        })}

        <g clipPath={animate ? `url(#${clipPathId})` : undefined}>
          {/* Filled area under the line */}
          {showFill && (
            <path
              d={`${path} L 100,100 L 0,100 Z`}
              fill={`url(#${fillGradientId})`}
              stroke='none'
            />
          )}

          {/* Main line */}
          <path
            d={path}
            fill='none'
            stroke={`url(#${lineGradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap='round'
            strokeLinejoin='round'
            vectorEffect='non-scaling-stroke'
          />
        </g>

        {/* Invisible hover line */}
        <path
          d={path}
          fill='none'
          stroke='transparent'
          strokeWidth='10'
          className='gradient-line-chart-hover-path'
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          role={onClick ? 'button' : undefined}
          aria-label={onClick ? 'Click to interact with chart' : undefined}
          tabIndex={onClick ? 0 : undefined}
          onKeyDown={
            onClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const svg = e.currentTarget.ownerSVGElement;

                    if (svg) {
                      const rect = svg.getBoundingClientRect();
                      const centerX = rect.width / 2;
                      const centerY = rect.height / 2;

                      onClick({ x: centerX, y: centerY });
                    }
                  }
                }
              : undefined
          }
        />
      </svg>
    </div>
  );
}

interface AxisLabel {
  /** The display text for this tick (e.g. "3000", "Jan '26") */
  label: string;
  /** Position along the axis as a percentage 0–100 */
  value: number;
}

interface AxisConfig {
  /** Array of tick labels to render */
  labels: AxisLabel[];
  /** Show dashed grid lines at each tick (default: true) */
  showGridLines?: boolean;
  /** Color of the axis labels (default: '#ffffff' in dark mode, '#374151' in light mode) */
  labelColor?: string;
  /** Color of the grid lines (default: '#2a3444') */
  gridLineColor?: string;
  /** Font size in viewBox units for labels (default: 3.6) */
  fontSize?: number;
}

interface DataPoint {
  x: number;
  y: number;
}

interface GradientStop {
  offset: string;
  color: string;
  opacity?: number;
}

interface LineChartProps {
  /** Array of data points with x and y coordinates (0-100 range) */
  data: DataPoint[];
  /** Width of the container (default: '200px') */
  width?: string;
  /** Height of the SVG (default: 96) */
  height?: number;
  /** SVG viewBox (default: '0 0 100 100') */
  viewBox?: string;
  /** Container margin (default: '20px') */
  margin?: string;
  /** Line gradient stops for stroke color */
  lineGradient?: GradientStop[];
  /** Fill gradient stops for area under curve */
  fillGradient?: GradientStop[];
  /** Stroke width of the line (default: 2) */
  strokeWidth?: number;
  /** Animation duration in seconds (default: 2) */
  animationDuration?: number;
  /** Whether to show the filled area under the line (default: true) */
  showFill?: boolean;
  /** Whether to animate the chart reveal (default: true) */
  animate?: boolean;
  /** Enable dark mode styling (default: false) */
  darkMode?: boolean;
  /** Custom CSS class for styling */
  className?: string;
  /** Unique ID prefix for gradients to avoid conflicts (default: random) */
  idPrefix?: string;
  /** Callback function triggered when the line chart is clicked */
  onClick?: (coordinates: { x: number; y: number }) => void;
  /** Y-axis configuration — labels on the left, horizontal grid lines */
  yAxis?: AxisConfig;
  /** X-axis configuration — labels on the bottom, vertical grid lines */
  xAxis?: AxisConfig;
}

/**
 * Generates SVG path string from data points
 * @param points - Array of data points with x,y coordinates
 * @returns SVG path string
 */
const generatePath = (points: DataPoint[]): string => {
  if (points.length === 0) {
    return '';
  }

  let path = `M ${points[0].x},${100 - points[0].y}`;

  for (let i = 1; i < points.length; i += 1) {
    path += ` L ${points[i].x},${100 - points[i].y}`;
  }

  return path;
};

GradientLineChart.defaultProps = DEFAULT_PROPS;

export default GradientLineChart;
